/**
 * Floor Plan Detail Footer DOM Component
 *
 * Renders the footer with pricing information and CTA buttons.
 * Displays a fixed footer pinned to the bottom of the detail sheet.
 *
 * @module nativeInteractiveSiteMap/components/floorPlanDetailFooter.dom
 */

import fastdom from 'fastdom';
import getLeadLotAttributes from '../services/leadLotAttributesService';

/**
 * Renders the "From $X" pricing display block. Prefers the pre-formatted price from Redux,
 * falling back to formatting the raw price only when it's actually present (price can be null
 * per the inventory mapping).
 * @param {Object} floorPlan
 * @returns {string} HTML string.
 */
function getPricingDisplay(floorPlan) {
  const displayPrice = floorPlan.price ? `$${floorPlan.price.toLocaleString()}` : null;

  return `
    <div class="nhs-u-display--flex nhs-u-items-center nhs-u-justify-content-space-between nhs-u-gap--200">
      <span class="nhs-mq-native-interactive-site-map__footer-label-large nhs-o-typography--sans-body-1-small-regular nhs-u-color--black-1000">
        From
      </span>
      <span class="nhs-mq-native-interactive-site-map__footer-label-large nhs-o-typography--sans-body-1-medium-regular nhs-u-color--black-1000">
        ${displayPrice ?? ''}
      </span>
    </div>
  `;
}

/**
 * Creates the Floor Plan Detail Footer component
 * @param {Object} floorPlan - The currently selected floor plan with pricing
 * @param {Object} community - The community context data
 * @param {Object} [lot] - The currently selected lot, if any (NHS20-8974 Lead API params).
 * @param {string|null} [elevationTitle] - The elevation currently selected in the elevation
 *   selector, if any (NHS20-8974 Lead API params).
 * @param {string|null} [structuralOptions] - The structural options currently selected, pipe
 *   delimited, if any. No selector exists yet to source this from — always null until one is
 *   built; the plumbing below is ready to carry a real value through to the Lead API as soon
 *   as it is.
 * @returns {{ getElement: Function, remove: Function }}
 */
export default function createFloorPlanDetailFooterDom(
  floorPlan = null,
  community = null,
  lot = null,
  elevationTitle = null,
  structuralOptions = null,
) {
  const state = { root: null };

  function initializeDom() {
    state.root = document.createElement('div');
    state.root.className = 'nhs-c-native-interactive-site-map__floorplan-detail-footer';
    state.root.setAttribute('data-card', '');

    // Defensive: Handle null floorPlan gracefully
    if (!floorPlan) {
      return;
    }

    const { lotNumber, lotSize, lotType, lotFacing } = getLeadLotAttributes(lot);

    // These data-* attributes are read by leadGenerators/components/wrapper.js's global click
    // delegation as the clicked element's own dataset, then spread into taskFlow.js's
    // initialState() — which is how uiState.modalInformation ends up with the id/type/community
    // context GetMoreInformationForm (NHS20-8178) needs to post a real lead. 'plan' matches
    // leadConstructor's 'plan-information' key (services/leadConstructor.js), which reads
    // formData.id as models/planInformationLead.js's planId. The lot/elevation attributes feed
    // the Lead API's LotNumber/LotSize/LotType/LotFacing/Elevation parameters (NHS20-8974).
    state.root.innerHTML = `
      <div class="nhs-u-flex-column nhs-u-gap--500 nhs-u-width--full">
        ${getPricingDisplay(floorPlan)}
        <div class="nhs-u-display--flex nhs-u-gap--300">
          <button class="nhs-c-button nhs-c-button--medium-40 nhs-c-button--primary-lead nhs-u-flex--1" type="button"
            data-lead-generator-opener="request-information"
            data-from-page="CommunityDetail.NativeInteractiveSiteMap.Plan"
            data-id="${floorPlan.planId ?? ''}"
            data-type="plan"
            data-community-id="${community?.communityId ?? ''}"
            data-community-name="${community?.communityName ?? ''}"
            data-market-id="${community?.marketId ?? ''}"
            data-market-name="${community?.marketName ?? ''}"
            data-brand-name="${community?.brandName ?? ''}"
            data-lot-number="${lotNumber ?? ''}"
            data-lot-size="${lotSize ?? ''}"
            data-lot-type="${lotType ?? ''}"
            data-lot-facing="${lotFacing ?? ''}"
            data-elevation="${elevationTitle ?? ''}"
            data-structural-options="${structuralOptions ?? ''}"
          >
            Contact builder
          </button>
        </div>
      </div>
    `;
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    remove() {
      fastdom.mutate(() => {
        state.root?.remove();
        state.root = null;
      });
    },
  };
}
